import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { MEDIA_QUERIES } from 'shared/styles/variables';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import FormOrganizationSessionLength, {
  FormOrganizationSessionLengthData,
} from 'dashboard/forms/FormOrganizationSessionLength';
import { FormSamlMetadataUrlData } from 'dashboard/forms/FormSamlMetadataUrl/FormSamlMetadataUrl';
import { FormSamlMetadataXmlData } from 'dashboard/forms/FormSamlMetadataXml/FormSamlMetadataXml';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { IdpMetadata, Organization } from 'dashboard/models/organization';
import { MfaMethod, User } from 'dashboard/models/user';
import AddSamlInformationModal from 'dashboard/views/DashboardOrganizationSecurityView/AddSamlInformationModal';
import ExcessPermissionsModal from 'dashboard/views/DashboardOrganizationSecurityView/ExcessPermissionsModal';
import MfaRequirementModal from 'dashboard/views/DashboardOrganizationSecurityView/MfaRequirementModal';
import SamlRequirementModal from 'dashboard/views/DashboardOrganizationSecurityView/SamlRequirementModal';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { ABBR_DATE_FORMAT } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { permissionTableMapping } from 'lib/permissionTableMapping';
import { Iso8601DateString } from 'lib/types';

export interface DashboardOrganizationSecurityViewProps {
  handleSamlParseUrl: (values: FormSamlMetadataUrlData) => Promise<void>;
  handleSamlParseXml: (values: FormSamlMetadataXmlData) => Promise<void>;
  organization: Organization;
  updateOrganizationSessionLength: (values: FormOrganizationSessionLengthData) => Promise<void>;
  updateEnabledMfaMethods: (enabledMfaMethods: MfaMethod[]) => Promise<void>;
  updateMfaRequired: (requiredAt?: Iso8601DateString) => Promise<void>;
  updateRefreshActiveSessions: (shouldRefresh: boolean) => Promise<void>;
  updateSamlRequired: (requiredAt?: Iso8601DateString) => Promise<void>;
  users: User[];
  currentUser: User;
  IAMpageSize: number;
  IAMpaginationMeta: PaginationParams;
  handleIAMPageSizeClick: (pageSize: number) => void;
  handleIAMPageLinkClick: (pageNumber: number) => void;
}

const DashboardOrganizationSecurityView = ({
  handleSamlParseUrl,
  handleSamlParseXml,
  organization,
  updateOrganizationSessionLength,
  updateEnabledMfaMethods,
  updateMfaRequired,
  updateRefreshActiveSessions,
  updateSamlRequired,
  users,
  currentUser,
  IAMpageSize,
  IAMpaginationMeta,
  handleIAMPageLinkClick,
  handleIAMPageSizeClick,
}: DashboardOrganizationSecurityViewProps) => {
  const canEditOrganization = usePermission(Permission.OrganizationWrite);
  const hasMFAPermission = usePermission(Permission.UserMfaAllUsersView);

  const {
    'has-saml-enabled': hasSamlEnabled,
    'mfa-required-at': mfaRequiredAt,
    'saml-required-at': samlRequiredAt,
    'enabled-mfa-methods': enabledMfaMethods,
    'idp-metadata': idpMetadata,
  } = organization.attributes;

  const canEditOrganzationMFA = canEditOrganization && hasMFAPermission;

  return (
    <Container>
      <Header title='Security' />
      <Section>
        <SsoCard
          organization={organization}
          hasPermission={canEditOrganization}
          hasSamlEnabled={hasSamlEnabled}
          samlRequiredAt={samlRequiredAt}
          idpMetadata={idpMetadata}
          updateSamlRequired={updateSamlRequired}
          handleSamlParseUrl={handleSamlParseUrl}
          handleSamlParseXml={handleSamlParseXml}
        />

        <MfaConfigurationCard
          users={users}
          currentUser={currentUser}
          mfaRequiredAt={mfaRequiredAt}
          enabledMfaMethods={enabledMfaMethods}
          hasPermission={canEditOrganzationMFA}
          updateMfaRequired={updateMfaRequired}
          updateEnabledMfaMethods={updateEnabledMfaMethods}
        />

        <SessionLengthCard
          organization={organization}
          updateOrganizationSessionLength={updateOrganizationSessionLength}
          updateRefreshActiveSessions={updateRefreshActiveSessions}
        />

        <FeatureFlagged featureFlag='dashboard:show-overprovisioned-iam'>
          <IAMOverviewCard
            users={users}
            IAMpageSize={IAMpageSize}
            IAMpaginationMeta={IAMpaginationMeta}
            handleIAMPageSizeClick={handleIAMPageSizeClick}
            handleIAMPageLinkClick={handleIAMPageLinkClick}
          />
        </FeatureFlagged>
      </Section>
    </Container>
  );
};

const OrganizationCard = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  @media ${MEDIA_QUERIES.isMobile} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const ButtonSection = styled(Card.Body)`
  display: flex;
  align-items: center;
`;

const SsoCard = ({
  organization,
  hasPermission,
  hasSamlEnabled,
  samlRequiredAt,
  idpMetadata,
  updateSamlRequired,
  handleSamlParseUrl,
  handleSamlParseXml,
}: {
  organization: Organization;
  hasPermission: boolean;
  hasSamlEnabled: boolean;
  samlRequiredAt: Iso8601DateString | null;
  idpMetadata: IdpMetadata | null;
  updateSamlRequired: (requiredAt?: Iso8601DateString) => Promise<void>;
  handleSamlParseUrl: (values: FormSamlMetadataUrlData) => Promise<void>;
  handleSamlParseXml: (values: FormSamlMetadataXmlData) => Promise<void>;
}) => {
  const [
    showAddSamlInformationModal,
    { enable: openAddSamlInformationModal, disable: hideAddSamlInformationModal },
  ] = useToggle(false);

  const handleSamlParseUrlAndClose = useCallback(
    async (values: FormSamlMetadataUrlData) => {
      await handleSamlParseUrl(values);
      hideAddSamlInformationModal();
    },
    [handleSamlParseUrl],
  );

  const handleSamlParseXmlAndClose = useCallback(
    async (values: FormSamlMetadataXmlData) => {
      await handleSamlParseXml(values);
      hideAddSamlInformationModal();
    },
    [handleSamlParseXml],
  );

  const [
    showSamlRequirementModal,
    { enable: openSamlRequirementModal, disable: hideSamlRequirementModal },
  ] = useToggle(false);

  const [
    showRemoveSamlRequirementModal,
    { prompt, confirm, cancel: hideRemoveSamlRequirementModal },
  ] = useConfirmation();

  const openRemoveSamlRequirementConfirmationModal = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => prompt(() => updateSamlRequired(null)),
    [],
  );

  return (
    <>
      <ModalWrapper
        isOpen={showAddSamlInformationModal}
        handleCloseModal={hideAddSamlInformationModal}
      >
        <AddSamlInformationModal
          organization={organization}
          handleClose={hideAddSamlInformationModal}
          handleSamlParseUrl={handleSamlParseUrlAndClose}
          handleSamlParseXml={handleSamlParseXmlAndClose}
        />
      </ModalWrapper>
      <ModalWrapper isOpen={showSamlRequirementModal} handleCloseModal={hideSamlRequirementModal}>
        <SamlRequirementModal
          handleClose={hideSamlRequirementModal}
          handleSubmit={updateSamlRequired}
        />
      </ModalWrapper>
      <ModalWrapper
        isOpen={showRemoveSamlRequirementModal}
        handleCloseModal={hideRemoveSamlRequirementModal}
      >
        <ConfirmationModal
          cancelAction={hideRemoveSamlRequirementModal}
          confirmAction={confirm}
          header='Remove SAML enforcement?'
          confirmActionText='Remove'
          confirmButtonType='primary'
        >
          SAML authentication will no longer be required to sign in.
        </ConfirmationModal>
      </ModalWrapper>
      <OrganizationCard>
        {hasSamlEnabled ? (
          <Card data-test='organization__saml-card'>
            <Card.Body>
              <Card.Title>Single sign-on</Card.Title>
              <Paragraph>
                SAML has been enabled for your organization. Use the slug{' '}
                <MonoSpan>{organization.attributes.slug}</MonoSpan> to sign in.
              </Paragraph>
              <CodeHighlight id='organization__idp-metadata' language='json' rowsToShow={20}>
                {JSON.stringify(idpMetadata, null, 2)}
              </CodeHighlight>
              <Spacer height={2} />
              <Button
                id='organization__add-saml-info-button'
                data-test='organization__add-saml-info-button'
                disabled={!hasPermission}
                onClick={openAddSamlInformationModal}
                width='button'
              >
                Edit SAML Configuration
              </Button>
            </Card.Body>
            <Card.Body>
              <TableLayout
                dataTest='organization__enforce-saml-table'
                headerTR={
                  <>
                    <TableTH>Enforcement</TableTH>
                    <TableTH>{/*Toggle*/}</TableTH>
                  </>
                }
                nullState={null}
              >
                <TableTR id='enforce-saml-table-row' data-test='enforce-saml-table-row'>
                  <TableTD>
                    <Paragraph>Enforce SAML authentication for the entire organization.</Paragraph>
                    {samlRequiredAt != null && (
                      <Paragraph data-test='enforce-saml-table-row__enforced-at-message'>
                        {DateTime.local() > DateTime.fromISO(samlRequiredAt)
                          ? `Enforced since ${DateTime.fromISO(samlRequiredAt).toFormat(
                              ABBR_DATE_FORMAT,
                            )}.`
                          : `Enforcement will begin on ${DateTime.fromISO(samlRequiredAt).toFormat(
                              ABBR_DATE_FORMAT,
                            )}.`}
                      </Paragraph>
                    )}
                  </TableTD>
                  <ToggleTD>
                    <ToggleSlider
                      data-test='enforce-saml-table-row--toggle'
                      disabled={!hasPermission}
                      onChange={
                        samlRequiredAt == null
                          ? openSamlRequirementModal
                          : openRemoveSamlRequirementConfirmationModal
                      }
                      value={samlRequiredAt != null}
                      titleOn='Enabled'
                      labelOn='Enabled'
                      titleOff='Disabled'
                      labelOff='Disabled'
                    />
                  </ToggleTD>
                </TableTR>
              </TableLayout>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Card.Body>
              <Card.Title>Single sign-on</Card.Title>
              <Paragraph>
                Enable single sign on for your organization by registering your identity provider.
              </Paragraph>
            </Card.Body>
            <ButtonSection>
              <Button
                id='organization__add-saml-info-button'
                data-test='organization__add-saml-info-button'
                disabled={!hasPermission}
                onClick={openAddSamlInformationModal}
                width='button'
              >
                Set up
              </Button>
            </ButtonSection>
          </>
        )}
      </OrganizationCard>
    </>
  );
};

interface MfaConfigurationCardProps {
  users: User[];
  currentUser: User;
  mfaRequiredAt: Iso8601DateString | null;
  enabledMfaMethods: MfaMethod[];
  hasPermission: boolean;
  updateMfaRequired: (requiredAt?: Iso8601DateString) => Promise<void>;
  updateEnabledMfaMethods: (enabledMfaMethods: MfaMethod[]) => Promise<void>;
}

const MfaConfigurationCard = ({
  users,
  currentUser,
  mfaRequiredAt,
  enabledMfaMethods,
  hasPermission,
  updateMfaRequired,
  updateEnabledMfaMethods,
}: MfaConfigurationCardProps) => {
  const [
    showMfaRequirementModal,
    { enable: openMfaRequirementModal, disable: hideMfaRequirementModal },
  ] = useToggle(false);

  const [
    showRemoveMfaRequirementModal,
    { prompt, confirm, cancel: hideRemoveMfaRequirementModal },
  ] = useConfirmation();

  const openRemoveMfaRequirementConfirmationModal = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => prompt(() => updateMfaRequired(null)),
    [],
  );

  const handleToggleMfaMethod = useCallback(
    (mfaMethod: MfaMethod, enabled: boolean) =>
      updateEnabledMfaMethods(
        enabled
          ? enabledMfaMethods.concat(mfaMethod)
          : enabledMfaMethods.filter((method) => method !== mfaMethod),
      ),
    [enabledMfaMethods],
  );

  const enabledMfaMethodsSet = new Set(enabledMfaMethods);

  return (
    <>
      <ModalWrapper isOpen={showMfaRequirementModal} handleCloseModal={hideMfaRequirementModal}>
        <MfaRequirementModal
          users={users}
          currentUser={currentUser}
          handleClose={hideMfaRequirementModal}
          handleSubmit={updateMfaRequired}
        />
      </ModalWrapper>
      <ModalWrapper
        isOpen={showRemoveMfaRequirementModal}
        handleCloseModal={hideRemoveMfaRequirementModal}
      >
        <ConfirmationModal
          cancelAction={hideRemoveMfaRequirementModal}
          confirmAction={confirm}
          header='Remove 2FA enforcement?'
          confirmActionText='Remove'
          confirmButtonType='primary'
        >
          2-factor authentication will no longer be required to sign in.
        </ConfirmationModal>
      </ModalWrapper>
      <Card data-test='organization__mfa-card'>
        <Card.Body>
          <Card.Title>2-factor authentication</Card.Title>
          <Paragraph>
            Enable an extra layer of security for your entire organization by enforcing 2-factor
            authentication, and choosing which methods are permitted for members of your team.
          </Paragraph>
        </Card.Body>
        <Card.Body>
          <TableLayout
            dataTest='organization__enabled-mfa-methods-table'
            headerTR={
              <>
                <TableTH>Permitted 2-factor methods</TableTH>
                <TableTH>{/*Description*/}</TableTH>
                <TableTH>{/*Status*/}</TableTH>
              </>
            }
            nullState={null}
          >
            <TableTR
              id='enabled-mfa-methods-table-row__totp'
              data-test='enabled-mfa-methods-table-row__totp'
            >
              <TableTD>One-time password</TableTD>
              <TableTD>
                <span>
                  Allow 2-factor authentication via an application on your phone.
                  <HelpIcon>
                    <HelpMessage>
                      We recommend using an application such as{' '}
                      <a href='https://authy.com/' target='_blank' rel='noreferrer'>
                        Authy
                      </a>
                      ,{' '}
                      <a href='https://1password.com/' target='_blank' rel='noreferrer'>
                        1Password
                      </a>
                      , or{' '}
                      <a href='https://lastpass.com/auth/' target='_blank' rel='noreferrer'>
                        LastPass Authenticator
                      </a>
                      . These applications support secure backup of your authentication codes in the
                      cloud and can be restored if you lose access to your device.
                    </HelpMessage>
                  </HelpIcon>
                </span>
              </TableTD>
              <ToggleTD>
                <ToggleSlider
                  data-test='enabled-mfa-methods-table-row__totp--toggle'
                  disabled={!hasPermission}
                  onChange={(value) => handleToggleMfaMethod(MfaMethod.Totp, value)}
                  value={enabledMfaMethodsSet.has(MfaMethod.Totp)}
                  titleOn='Enabled'
                  labelOn='Enabled'
                  titleOff='Disabled'
                  labelOff='Disabled'
                />
              </ToggleTD>
            </TableTR>
            <TableTR
              id='enabled-mfa-methods-table-row__email'
              data-test='enabled-mfa-methods-table-row__email'
            >
              <TableTD>Email</TableTD>
              <TableTD>Allow 2-factor authentication via an email confirmation link.</TableTD>
              <ToggleTD>
                <ToggleSlider
                  data-test='enabled-mfa-methods-table-row__email--toggle'
                  disabled={!hasPermission}
                  onChange={(value) => handleToggleMfaMethod(MfaMethod.Email, value)}
                  value={enabledMfaMethodsSet.has(MfaMethod.Email)}
                  titleOn='Enabled'
                  labelOn='Enabled'
                  titleOff='Disabled'
                  labelOff='Disabled'
                />
              </ToggleTD>
            </TableTR>
            <TableTR
              id='enabled-mfa-methods-table-row__phone'
              data-test='enabled-mfa-methods-table-row__phone'
            >
              <TableTD>Phone</TableTD>
              <TableTD>Allow 2-factor authentication via a SMS confirmation code.</TableTD>
              <ToggleTD>
                <ToggleSlider
                  data-test='enabled-mfa-methods-table-row__phone--toggle'
                  disabled={!hasPermission}
                  onChange={(value) => handleToggleMfaMethod(MfaMethod.Phone, value)}
                  value={enabledMfaMethodsSet.has(MfaMethod.Phone)}
                  titleOn='Enabled'
                  labelOn='Enabled'
                  titleOff='Disabled'
                  labelOff='Disabled'
                />
              </ToggleTD>
            </TableTR>
          </TableLayout>
        </Card.Body>
        <Card.Body>
          <TableLayout
            dataTest='organization__enforce-mfa-table'
            headerTR={
              <>
                <TableTH>Enforcement</TableTH>
                <TableTH>{/*Toggle*/}</TableTH>
              </>
            }
            nullState={null}
          >
            <TableTR id='enforce-mfa-table-row' data-test='enforce-mfa-table-row'>
              <TableTD>
                <Paragraph>Enforce 2-factor authentication for the entire organization.</Paragraph>
                {mfaRequiredAt != null && (
                  <Paragraph data-test='enforce-mfa-table-row__enforced-at-message'>
                    {DateTime.local() > DateTime.fromISO(mfaRequiredAt)
                      ? `Enforced since ${DateTime.fromISO(mfaRequiredAt).toFormat(
                          ABBR_DATE_FORMAT,
                        )}.`
                      : `Enforcement will begin on ${DateTime.fromISO(mfaRequiredAt).toFormat(
                          ABBR_DATE_FORMAT,
                        )}.`}
                  </Paragraph>
                )}
              </TableTD>
              <ToggleTD>
                <ToggleSlider
                  data-test='enforce-mfa-table-row--toggle'
                  disabled={!hasPermission}
                  onChange={
                    mfaRequiredAt == null
                      ? openMfaRequirementModal
                      : openRemoveMfaRequirementConfirmationModal
                  }
                  value={mfaRequiredAt != null}
                  titleOn='Enabled'
                  labelOn='Enabled'
                  titleOff='Disabled'
                  labelOff='Disabled'
                />
              </ToggleTD>
            </TableTR>
          </TableLayout>
        </Card.Body>
      </Card>
    </>
  );
};

const SessionLengthCard = ({
  organization,
  updateOrganizationSessionLength,
  updateRefreshActiveSessions,
}: {
  organization: Organization;
  updateOrganizationSessionLength: (values: FormOrganizationSessionLengthData) => Promise<void>;
  updateRefreshActiveSessions: (shouldRefresh: boolean) => Promise<void>;
}) => {
  return (
    <OrganizationCard>
      <Card.Body>
        <Card.Title>Session Length</Card.Title>
        <Paragraph>Customize the length of your user sessions.</Paragraph>
        <FormOrganizationSessionLength
          initialValues={organization.attributes}
          onSubmit={updateOrganizationSessionLength}
        />
        <FeatureFlagged featureFlag='dashboard:refresh-active-sessions'>
          <TableLayout
            dataTest='organization__refresh-active-sessions'
            headerTR={
              <>
                <TableTH>Enforcement</TableTH>
                <TableTH>{/*Toggle*/}</TableTH>
              </>
            }
            nullState={null}
          >
            <TableTR
              id='refresh-active-sessions-table-row'
              data-test='refresh-active-sessions-table-row'
            >
              <TableTD>
                <Paragraph>
                  <span>
                    Refresh actively used dashboard sessions.
                    <HelpIcon>
                      <HelpMessage>
                        If a user is actively using the dashboard, their session will automatically
                        be renewed when it hits the time limit, instead of being logged out.
                      </HelpMessage>
                    </HelpIcon>
                  </span>
                </Paragraph>
              </TableTD>
              <ToggleTD>
                <ToggleSlider
                  data-test='refresh-active-sessions-table-row--toggle'
                  onChange={(value) => updateRefreshActiveSessions(value)}
                  value={organization.attributes['refresh-session-on-activity']}
                  titleOn='Enabled'
                  labelOn='Enabled'
                  titleOff='Disabled'
                  labelOff='Disabled'
                />
              </ToggleTD>
            </TableTR>
          </TableLayout>
        </FeatureFlagged>
      </Card.Body>
    </OrganizationCard>
  );
};

function dashboardPermissions(permissions: string[]): string[] {
  const result: string[] = [];

  return Object.entries(permissionTableMapping)
    .filter(([_, values]) => values.every((permission) => permissions.includes(permission)))
    .map(([key]) => key);

  return result;
}

const IAMTableRows = (users: User[]) => {
  const [
    showExcessPermissionsModal,
    { enable: openExcessPermissionsModal, disable: hideExcessPermissionsModal },
  ] = useToggle(false);

  const [excessPermissionsList, setExcessPermissionsList] = useState<string[]>([]);

  const handleToggleExcessPermissionsModal = useCallback((excessPermissionsList: string[]) => {
    openExcessPermissionsModal();
    setExcessPermissionsList(excessPermissionsList);
  }, []);

  const handleCloseModal = useCallback(() => {
    hideExcessPermissionsModal();
    setExcessPermissionsList([]);
  }, []);

  return (
    <>
      {showExcessPermissionsModal && (
        <ModalWrapper isOpen={showExcessPermissionsModal} handleCloseModal={handleCloseModal}>
          <ExcessPermissionsModal
            excessPermissionsList={excessPermissionsList}
            handleClose={handleCloseModal}
          />
        </ModalWrapper>
      )}

      {users.flatMap((user) => {
        const roles = user.attributes['excess-permissions'] || [];

        return roles.map((excessPermissions, roleIndex) => {
          const excess = dashboardPermissions(excessPermissions['excess-permissions-list'] || []);
          const rolePermissions = dashboardPermissions([
            ...excessPermissions['role-permissions-list'],
          ]);
          const excessCount = excess.length || 0;
          const rolePermissionCount = rolePermissions.length || 0;
          const excessRatio = `${excessCount}/${rolePermissionCount}`;

          const handleModalOpen = () => {
            handleToggleExcessPermissionsModal(excess);
          };

          return (
            <TableTR key={`${user.id}-${roleIndex}`}>
              <TableTD>{roleIndex === 0 ? user.attributes['email-address'] : ''}</TableTD>
              <TableTD>{excessPermissions['role-name']}</TableTD>
              <TableTD>
                <Button
                  id='organization__show-excess-permissions-button'
                  onClick={() => {
                    handleModalOpen();
                  }}
                >
                  {excessRatio}
                </Button>
              </TableTD>
            </TableTR>
          );
        });
      })}
    </>
  );
};

const IAMOverviewCard = ({
  users,
  IAMpageSize,
  IAMpaginationMeta,
  handleIAMPageLinkClick,
  handleIAMPageSizeClick,
}: {
  users: User[];
  IAMpageSize: number;
  IAMpaginationMeta: PaginationParams;
  handleIAMPageLinkClick: (pageNumber: number) => void;
  handleIAMPageSizeClick: (pageSize: number) => void;
}) => {
  return (
    <OrganizationCard>
      <Card.Body>
        <Card.Title>IAM Overview</Card.Title>
        <Paragraph>
          Overview of user roles, and any excess permissions from them. A permission is considered
          in excess if it has not been used by that user in the last 3 months.
        </Paragraph>
        <TableLayout
          dataTest='organization__iam-overview-table'
          headerTR={
            <>
              <TableTH>User</TableTH>
              <TableTH>Roles</TableTH>
              <TableTH>Excess Amount</TableTH>
            </>
          }
          nullState={null}
        >
          {IAMTableRows(users)}
        </TableLayout>
        <Pagination
          pageSize={IAMpageSize}
          paginationParams={IAMpaginationMeta}
          handlePageLinkClick={handleIAMPageLinkClick}
          handlePageSizeClick={handleIAMPageSizeClick}
        />
      </Card.Body>
    </OrganizationCard>
  );
};

const ToggleTD = styled(TableTD)`
  width: 160px;
`;

const HelpMessage = styled.span`
  /* override global anchor styles */
  > * {
    font-size: inherit;
    font-weight: inherit;
  }
`;

export default DashboardOrganizationSecurityView;
